import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HardHat, ArrowRight, Check, Clock, AlertTriangle, Star, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';

const HERO_IMAGE = "https://images.pexels.com/photos/3990359/pexels-photo-3990359.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.pexels.com/photos/3616746/pexels-photo-3616746.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const NettoyageFinDeChantierPage = () => {
  const questions = [
    { id: 'surface', label: 'Surface du chantier ?', type: 'text', placeholder: 'Ex: 120 m²' },
    { id: 'type_travaux', label: 'Type de travaux réalisés ?', type: 'select', options: ['Rénovation complète', 'Peinture', 'Carrelage/Sol', 'Plâtrerie', 'Construction neuve', 'Autre'] },
    { id: 'etat', label: 'État actuel ?', type: 'select', options: ['Poussière légère', 'Très poussiéreux', 'Gravats présents', 'Je ne sais pas'] },
    { id: 'delai', label: 'Date de livraison ?', type: 'select', options: ['Urgent (2-3 jours)', 'Cette semaine', 'Semaine prochaine', 'Flexible'] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* PILIER 1: ATTENTION */}
      <section className="hero-section relative min-h-[70vh] flex items-center">
        <img src={HERO_IMAGE} alt="Chantier en cours de rénovation" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <HardHat className="h-10 w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">Homely Nettoyage</Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              Fin de chantier impeccable, livraison sereine
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl">
              Poussière de plâtre, résidus de peinture, traces de ciment : 
              nous transformons votre chantier en espace prêt à vivre.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock className="h-5 w-5 text-yellow-400" />
                <span className="text-white text-sm">Intervention rapide</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Truck className="h-5 w-5 text-blue-400" />
                <span className="text-white text-sm">Évacuation déchets</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Check className="h-5 w-5 text-green-400" />
                <span className="text-white text-sm">Prêt pour réception</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-14 px-10 text-base rounded-full"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Devis express
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-white">
                <span className="text-2xl font-bold">Dès 15 CHF/m²</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 2: ÉMOTION */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
                <span className="text-orange-500 font-semibold">Artisans & Promoteurs</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Ne laissez pas le nettoyage compromettre votre livraison
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                La poussière de chantier s'infiltre partout : dans les prises, les joints, 
                les menuiseries... Un nettoyage amateur peut prendre des jours et laisser 
                des défauts visibles le jour de la réception.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { title: 'Poussière fine omniprésente', desc: 'Le plâtre et le ciment créent une poussière très fine qui se dépose partout' },
                  { title: 'Risque de rayures', desc: 'Les résidus abrasifs peuvent endommager les surfaces neuves' },
                  { title: 'Délais serrés', desc: 'Le nettoyage est souvent la dernière étape avant livraison' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                    <span className="text-orange-500 font-bold text-xl">!</span>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img src={CONTENT_IMAGE_1} alt="Nettoyage fin de chantier" className="rounded-2xl shadow-2xl w-full" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <p className="font-bold text-2xl" style={{ color: 'var(--sage)' }}>Jusqu'à 3x</p>
                <p className="text-sm text-muted-foreground">plus rapide qu'un nettoyage amateur</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 3: MÉMOIRE - Ce qui est inclus */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Notre prestation</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Un nettoyage complet, méthodique et professionnel
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Dépoussiérage complet', items: ['Murs, plafonds, sols', 'Prises et interrupteurs', 'Radiateurs et grilles'] },
              { title: 'Nettoyage des surfaces', items: ['Sols (carrelage, parquet)', 'Menuiseries et huisseries', 'Sanitaires et robinetterie'] },
              { title: 'Finitions', items: ['Vitres intérieures', 'Traces de colle et ciment', 'Joints et plinthes'] }
            ].map((section, idx) => (
              <div key={idx} className="card-premium p-6">
                <h3 className="font-semibold mb-4" style={{ color: 'var(--sage-dark)' }}>{section.title}</h3>
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILIER 4: DÉCISION */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-12" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '300+', label: 'Chantiers nettoyés' },
              { value: '100%', label: 'Réceptions validées' },
              { value: '24-48h', label: 'Délai possible' },
              { value: 'Pro', label: 'Facture pour entreprise' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="bg-secondary/30 p-8 rounded-2xl max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-lg italic mb-4">
              "Nous faisons appel à Homely pour tous nos chantiers. Fiables, rapides et méticuleux. 
              Nos clients sont toujours impressionnés lors de la réception."
            </p>
            <p className="font-semibold">— Rénovations SA, Lausanne</p>
          </div>
        </div>
      </section>

      {/* PILIER 5: ACTION */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Demandez votre devis fin de chantier
            </h2>
            <p className="text-white/70">Devis détaillé sous 24h • Intervention express possible</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} />
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold mb-4">Autres services de nettoyage</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/nettoyage/canape" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage canapé</Link>
            <Link to="/nettoyage/terrasse" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage terrasse</Link>
            <Link to="/nettoyage/vitres" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage vitres</Link>
            <Link to="/nettoyage/apres-bail" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Après bail</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
